import { CheckCircleIcon, InfoIcon, WarningIcon, XCircleIcon } from '@phosphor-icons/react';
import { App } from 'antd';
import { type ReactNode, useMemo } from 'react';

import styles from './toast.module.scss';

export type ToastState = 'info' | 'success' | 'warning' | 'error';

export type ToastOptions = {
  title: string;
  description?: string;
  /** Rendered in the notification's action slot (e.g. a small <Button>). */
  action?: ReactNode;
  appearance?: 'light' | 'dark';
  /** Stable id; re-opening with the same key updates the toast instead of stacking. */
  key?: string;
  /** Seconds before auto-dismiss. 0 = sticky (no auto-dismiss). */
  duration?: number;
  showCloseIcon?: boolean;
};

const icons = {
  info: <InfoIcon size={16} weight="fill" className={styles.icon} />,
  success: (
    <CheckCircleIcon size={16} weight="fill" className={[styles.icon, styles.success].join(' ')} />
  ),
  warning: (
    <WarningIcon size={16} weight="fill" className={[styles.icon, styles.warning].join(' ')} />
  ),
  error: <XCircleIcon size={16} weight="fill" className={[styles.icon, styles.error].join(' ')} />,
} satisfies Record<ToastState, ReactNode>;

export function useToast() {
  const { notification } = App.useApp();
  return useMemo(() => {
    const open =
      (state: ToastState) =>
      ({
        title,
        description,
        action,
        appearance = 'dark',
        key,
        duration,
        showCloseIcon = true,
      }: ToastOptions) => {
        notification.open({
          key,
          duration,
          title: <span className={[styles.title, styles[state]].join(' ')}>{title}</span>,
          description: description ? (
            <span className={styles.description}>{description}</span>
          ) : undefined,
          icon: icons[state],
          actions: action,
          className: [styles.toast, appearance === 'dark' ? styles.dark : undefined]
            .filter(Boolean)
            .join(' '),
          classNames: { close: styles.closeButton, actions: styles.actions },
          closeIcon: showCloseIcon ? (
            <XCircleIcon size={22} className={styles.closeIcon} data-toast-close />
          ) : null,
        });
      };
    return {
      info: open('info'),
      success: open('success'),
      warning: open('warning'),
      error: open('error'),
      dismiss: (key: string) => notification.destroy(key),
    };
  }, [notification]);
}
